import { switchOrg } from '@/api/org-api'
// Deep path on purpose: `@/components/shared` is a wide barrel, and this module
// is pulled in by the route tree — importing the barrel here would drag every
// shared block into the app-boot module graph. Same reasoning as `api-error.ts`.
import toast from '@/components/shared/toasts'
import { queryClient } from '@/main'
import { useAuthStore } from '@/store/auth'
import { useInterventionFiltersStore } from '@/store/interventions'
import { useOrgStore } from '@/store/org'
import { UserRole, type Organization } from '@depanneo/types'
import { redirect } from '@tanstack/react-router'

/**
 * Make `org` the active organization for this browser.
 *
 * The cache wipe is security-critical, not a refresh nicety: every TanStack
 * Query entry was filled under the previous tenant, and the connector stamps
 * `x-organization-id` from this very store — leaving the old entries in place
 * would paint one org's screens with another org's rows.
 */
export function activateOrg(org: Organization) {
  queryClient.clear()
  useInterventionFiltersStore.getState().reset()
  useOrgStore.getState().setOrg(org)
}

/**
 * THE org guard for every `/org/$orgId/...` route.
 *
 * A member is pinned to their own org. A SUPER_ADMIN is not: the URL names the
 * org, so the URL wins. Opening a link into another org — from an outbound
 * Slack channel (#1551), from an email, or pasted by a colleague — activates
 * that org instead of bouncing to whichever one this browser happened to have
 * active. Bouncing is what sent every Slack deep link to the dashboard: a
 * super-admin's active org is almost never the org the event belongs to.
 *
 * `switchOrg` is the canonical door (the org switcher, the notification bell and
 * the global search all go through it): the server re-checks SUPER_ADMIN and
 * refuses an unknown, inactive or deleted org, so a URL can never activate an
 * org the API would then reject on every request.
 *
 * PASS THE ROUTE MATCH'S `abortController.signal`. The router aborts every
 * pending match the moment a newer navigation starts (`cancelMatches`), and a
 * superseded guard MUST land nothing: a super-admin who heads for org B and
 * turns back to A before the POST answers would otherwise end up reading A's
 * screens while every request carries B's `x-organization-id`.
 */
export async function ensureOrgAccess(
  orgId: string,
  signal?: AbortSignal
): Promise<void> {
  const { organizationId, role } = useAuthStore.getState()
  const { organizationId: activeOrgId } = useOrgStore.getState()

  if (role !== UserRole.SUPER_ADMIN) {
    // `/unauthorized` takes no params — the org that was refused is not part of
    // the target route.
    if (orgId !== organizationId) throw redirect({ to: '/unauthorized' })
    return
  }

  if (orgId === activeOrgId) return

  let org: Organization
  try {
    org = await switchOrg(orgId, signal)
  } catch (error) {
    // Cancelled because the user already navigated elsewhere: not an error, and
    // nothing to say. Falling through would toast and redirect on top of the
    // navigation they actually chose. (The superseded match may still run its
    // own `findOrg` loader — one GET for an org this super-admin may read,
    // cached under that org's own key, so it cannot surface anywhere else.)
    if (signal?.aborted) return

    // Refused or unreachable: say so out loud, then fall back to the org this
    // browser already had. A silent bounce is exactly what made the broken deep
    // link so hard to read.
    toast.error("Impossible d'ouvrir cette organisation")
    if (activeOrgId) {
      throw redirect({
        to: '/org/$orgId/dashboard',
        params: { orgId: activeOrgId },
      })
    }

    // Nothing to fall back to. `/` is NOT the answer: it forwards to the JWT
    // org's dashboard, whose guard runs this very switch, fails the same way and
    // lands back here — an endless bounce for as long as the API is down. Let
    // the error out instead; the route error boundary is a terminal state.
    throw error
  }

  // The answer can beat the abort by a hair — re-check before mutating global
  // state, or the active org outlives the navigation that asked for it.
  if (signal?.aborted) return

  activateOrg(org)
}
